import type { DragEvent, ReactNode, SyntheticEvent } from 'react';
import { useState } from 'react';

import AddOutlined from '@mui/icons-material/AddOutlined';
import ClearOutlined from '@mui/icons-material/ClearOutlined';
import { Box, Tab, Tabs as MuiTabs } from '@mui/material';
import { usePostHog } from 'posthog-js/react';

import { usePageInformationContext } from '@/context/PageInformationContext';

import { TooltipIconButton } from '../TooltipIconButton';

interface Tab {
  name: string;
  id?: string;
  disabled?: boolean;
  label?: ReactNode;
  closable?: boolean;
  onClose?: (value: number) => void;
  tag?: ReactNode;
}

interface CustomTabsProps {
  tabs: Tab[];
  activeTab: number;
  onChange?: (index: number) => void;
  onAddTab?: () => void;
  onReorder?: (sourceIndex: number, targetIndex: number) => void;
  height?: number;
  minTabWidth?: number;
  offset?: number;
  maxTabWidth?: number | 'unset';
  scrollButtons?: boolean | 'auto';
  disableRipple?: boolean;
}

const CloseButton = ({
  label,
  name,
  onClose,
  index
}: {
  label: ReactNode | undefined;
  index: number;
  name: string;
  onClose?: (value: number) => void;
}) => {
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
        {label || name}
      </Box>
      <TooltipIconButton
        component='span'
        data-tab-drag-disabled='true'
        sx={{
          height: 46,
          position: 'absolute',
          right: 0,
          backgroundColor: 'transparent',
          ':hover': {
            backgroundColor: 'action.hover'
          }
        }}
        title='Remove'
        onClick={e => {
          e.stopPropagation();
          if (onClose) onClose(index);
        }}
      >
        <ClearOutlined />
      </TooltipIconButton>
    </>
  );
};

const TAB_DRAG_DISABLE_SELECTOR = '[data-tab-drag-disabled="true"]';

const CustomTabs = ({
  tabs,
  activeTab,
  onChange,
  onAddTab,
  onReorder,
  height = 48,
  minTabWidth = 180,
  maxTabWidth = 'unset',
  offset = 0,
  scrollButtons = false,
  disableRipple = false,
  ...tabProps
}: CustomTabsProps) => {
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    if (onChange) onChange(newValue);
  };
  const isDraggable = Boolean(onReorder);

  const handleDragStart = (event: DragEvent, index: number) => {
    if (!onReorder) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest(TAB_DRAG_DISABLE_SELECTOR)) {
      event.preventDefault();
      return;
    }
    event.stopPropagation();
    setDraggingIndex(index);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragEnd = (event: DragEvent) => {
    if (!onReorder) return;
    event.stopPropagation();
    setDraggingIndex(null);
  };

  const handleDragOver = (event: DragEvent) => {
    if (!onReorder) return;
    event.preventDefault();
  };

  const handleDragEnter = (index: number) => {
    if (!onReorder) return;
  };

  const handleDrop = (event: DragEvent, targetIndex: number) => {
    if (!onReorder) return;
    event.preventDefault();
    event.stopPropagation();
    const sourceIndex = Number(event.dataTransfer.getData('text/plain'));
    setDraggingIndex(null);

    if (!Number.isNaN(sourceIndex) && sourceIndex !== targetIndex) {
      onReorder(sourceIndex, targetIndex);
    }
  };

  const { mainPageTitle, subPageTitle } = usePageInformationContext();

  const postHog = usePostHog();
  const handleTabClick = (
    mainPage: string | undefined,
    subPage: string | undefined,
    tabName: string
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- postHog can be null when not configured
    postHog?.capture('tab_click', {
      mainPage,
      subPage,
      tabName
    });
  };
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
      <MuiTabs
        aria-label='tabs'
        scrollButtons={scrollButtons}
        sx={{
          minWidth: 0,
          maxWidth: `calc(100% - ${offset}px)`,
          minHeight: `${height}px`,
          height,
          '& .MuiTabs-scrollButtons.Mui-disabled': {
            display: 'none'
          },
          '.MuiTabScrollButton-root': {
            width: 32,
            minWidth: 32,
            backgroundColor: 'gx.tileLight',
            color: 'gx.textDisabled',
            '&:hover': {
              color: 'white',
              backgroundColor: '#161b40'
            }
          }
        }}
        value={activeTab}
        variant='scrollable'
        onChange={handleChange}
      >
        {tabs.map(({ name, id, label, disabled, closable, onClose, tag }, index) => (
          <Tab
            key={id ?? `tab-${name}-${index}`}
            aria-controls={`tabpanel-${name}`}
            disabled={disabled}
            disableFocusRipple={disableRipple}
            disableRipple={disableRipple}
            id={`tab-${name}`}
            label={
              <Box
                data-dragging={isDraggable && draggingIndex === index ? 'true' : undefined}
                draggable={isDraggable}
                role='presentation'
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  width: '100%',
                  height: '100%',
                  textAlign: 'center',
                  cursor: isDraggable ? 'grab' : 'pointer',
                  borderRadius: '4px'
                }}
                onDragEnd={handleDragEnd}
                onDragEnter={() => {
                  handleDragEnter(index);
                }}
                onDragOver={handleDragOver}
                onDragStart={event => {
                  handleDragStart(event, index);
                }}
                onDrop={event => {
                  handleDrop(event, index);
                }}
                onMouseDownCapture={event => {
                  // Ensure tab change happens on interactive elements
                  // Capture phase fires before inputs handle the event
                  if (onChange && activeTab !== index) {
                    event.stopPropagation();
                    onChange(index);
                  }
                }}
              >
                {closable ? (
                  <CloseButton
                    index={index}
                    label={label}
                    name={name}
                    onClose={onClose}
                  />
                ) : (
                  <Box
                    sx={{
                      flexBasis: '100%'
                    }}
                  >
                    {label || name}
                  </Box>
                )}
                {tag}
              </Box>
            }
            sx={{
              alignItems: 'flex-start',
              textTransform: 'initial',
              height,
              minWidth: `${minTabWidth}px`,
              maxWidth: `${maxTabWidth}px`,
              minHeight: `${height}px`,
              p: 1,
              '&:not(:last-child)': { mr: '2px' }
            }}
            onClick={() => {
              handleTabClick(mainPageTitle, subPageTitle, name);
            }}
            {...tabProps}
          />
        ))}
      </MuiTabs>
      {onAddTab && (
        <TooltipIconButton
          sx={{ width: 32, minWidth: 32, backgroundColor: 'gx.tileLight' }}
          title='Add new page'
          onClick={onAddTab}
        >
          <AddOutlined sx={{ fontSize: '14px !important', color: 'gx.textDisabled' }} />
        </TooltipIconButton>
      )}
    </Box>
  );
};

export type { Tab };
export default CustomTabs;
